<script setup>
/* Analytics */
import {
  sendAnalyticsEvent,
  sanitizeAnalyticsString,
} from '../helpers/ga/analytics.js';

/* Helpers */
import { getProductPageUrl } from '../helpers/mainHelpers.js';

/* Composables */
import { useGenericAnalyticsDataHelper } from '../composables/useAnalyticsData.js';

/* Inject composables */
const { genericAnalyticsData } = useGenericAnalyticsDataHelper();

/* Props */
const props = defineProps({
  device: {
    type: Object,
    default: () => ({}),
  },
  compatibleDeviceIndex: {
    type: Number,
    default: 0,
  },
});

const deviceLink = getProductPageUrl(props.device?.link, window?.location?.origin);

/* Handle GA4 - Tealium */
const sendCompatibleDevicesTealiumEvent = (currDevice) => {
  const analyticsData = {
    ...genericAnalyticsData(
      'Compatible Devices',
      'Compatible Devices',
    ),
    index: props.compatibleDeviceIndex + 1,
    product_id: currDevice?.productId || 'NA',
    product_brand: window?.utag_data?.product_brand[0],
    product_name: sanitizeAnalyticsString(currDevice?.displayName) || 'NA',
    product_url: currDevice?.link || 'NA',
    // Note: We don't get the price for the Compatible Devices so we send them as 'NA'
    product_original_price: 'NA',
    product_price: 'NA',
    is_sale: 'NA',
    price_shown: 'NA',
    tealium_event: 'select_item',
  };

  sendAnalyticsEvent(analyticsData);
};
</script>

<template>
  <div>
    <a
      class="app__product__devices__section__link"
      :data-testid="`compatible-device-${device?.productId}`"
      :href="deviceLink"
      @click="sendCompatibleDevicesTealiumEvent(props.device)"
    >
      <div class="app__product__devices__section__card app__product__devices__section__drop-shadow">
        <div class="app__product__devices__section__card-content">
          <img
            :src="props.device?.image"
            :alt="props.device?.displayName"
            loading="lazy"
            width="130"
            height="130"
          >
          <g-heading
            class="app__product__devices__section__card-content__heading"
            text-align="center"
            size="4"
          >
            <h4 v-html="props.device?.displayName" />
          </g-heading>
        </div>
      </div>
    </a>
  </div>
</template>

<style lang="scss">
.app__product__devices__section {
  width: 100%;

  @include breakpoint(xs) {
    width: calc(50% - 0.75em);
  }

  @include breakpoint(md) {
    width: calc(25% - 1.125em);
  }

  &__link {
    display: block;
    position: relative;
    color: $color-black;
    text-decoration: none;
    height: 100%;

    &:visited,
    &:hover,
    &:active {
      color: $color-black !important; // stylelint-disable-line
      text-decoration: none;
    }
  }

  &__card {
    padding: 1em;
    cursor: pointer;
    height: 100%;

    @include breakpoint(sm) {
      padding: 1.3em 2em;
    }

    &-content {
      @include font-primary();
      display: flex;
      flex-flow: column;
      height: 100%;

      img {
        width: 130px;
        margin: 0 auto 0.5em;

        @include breakpoint(sm) {
          margin: 0 auto 1em;
        }
      }

      &__heading {
        margin: 0;
        word-break: break-word;
      }
    }
  }

  &__drop-shadow {
    height: 100%;
    width: 100%;
    padding-top: 1rem;
    @include make-drop-shadow();
  }
}
</style>
